import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LoadingSkeleton } from "@/components/Layout";
import { SeverityBadge, StatusPill } from "@/components/Badges";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/dates";
import {
  AlertOctagon, ArrowLeft, ArrowRight, Send, Sparkle, Shield, Target as TargetIcon,
  TrendingDown, AlertTriangle, Wrench, ListChecks, History as HistoryIcon,
  Pencil, Clock, User as UserIcon, DollarSign, Hash, CheckCircle2,
} from "lucide-react";
import RiskFormDialog from "@/components/RiskFormDialog";
import { severityFromScore, SEV_STYLES } from "@/lib/severity";

const SEV_HEX = { Critical: "#E11D48", High: "#EA580C", Moderate: "#F59E0B", Low: "#10B981" };
const _initials = (n = "") => n.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

function Mini5x5({ likelihood, impact, severity }) {
  // 5x5 grid with the current L×I cell highlighted. Shows at-a-glance where
  // the risk sits on the heatmap, way faster to read than two numbers.
  const color = SEV_HEX[severity] || "#64748B";
  return (
    <svg viewBox="0 0 60 60" className="h-14 w-14 shrink-0" aria-hidden>
      {Array.from({ length: 5 }).map((_, r) =>
        Array.from({ length: 5 }).map((_, c) => {
          // row 0 = top = impact 5; col 0 = left = likelihood 1
          const l = c + 1, i = 5 - r;
          const score = l * i;
          const cellSev = severityFromScore(score);
          const isCurrent = l === likelihood && i === impact;
          return (
            <rect
              key={`${r}-${c}`}
              x={c * 11.5 + 1} y={r * 11.5 + 1}
              width={10.5} height={10.5}
              rx={1.5}
              fill={isCurrent ? color : SEV_HEX[cellSev]}
              opacity={isCurrent ? 1 : 0.14}
              stroke={isCurrent ? "#0F172A" : "none"}
              strokeWidth={isCurrent ? 1.1 : 0}
            />
          );
        })
      )}
    </svg>
  );
}

function Sparkline({ points }) {
  const W = 240, H = 60, PAD = 6;
  const scores = points.map((p) => p.score || 0);
  const min = Math.min(...scores, 0), max = Math.max(...scores, 25);
  const stepX = (W - PAD * 2) / Math.max(points.length - 1, 1);
  const coords = points.map((p, i) => {
    const x = PAD + i * stepX;
    const y = PAD + (H - PAD * 2) * (1 - (p.score - min) / Math.max(max - min, 1));
    return [x, y, p];
  });
  const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${path} L${coords[coords.length - 1][0].toFixed(1)},${H - PAD} L${coords[0][0].toFixed(1)},${H - PAD} Z`;
  const currentColor = SEV_HEX[points[points.length - 1]?.severity] || "#0E7490";
  return (
    <div className="flex items-center gap-3">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-[240px] h-[60px]" data-testid="score-sparkline">
        <path d={areaPath} fill={currentColor} opacity="0.12" />
        <path d={path} fill="none" stroke={currentColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {coords.map(([x, y, p], i) => (
          <circle key={p.at || `pt-${i}`} cx={x} cy={y} r={i === coords.length - 1 ? 3.5 : 2.5} fill={SEV_HEX[p.severity] || "#64748B"}>
            <title>{`${p.at?.slice(0, 10) || "—"} · score ${p.score} · ${p.severity || "—"}`}</title>
          </circle>
        ))}
      </svg>
      <div className="text-right">
        <div className="text-[10px] font-mono uppercase text-slate-400 tracking-widest">Latest</div>
        <div className="font-mono font-extrabold text-2xl tabular-nums" style={{ color: currentColor }}>{points[points.length - 1].score}</div>
      </div>
    </div>
  );
}

function ScoreCard({ kicker, label, a, accent = "slate" }) {
  const score = a ? a.likelihood * a.impact : 0;
  const sev = severityFromScore(score);
  const s = SEV_STYLES[sev];
  const accentCls = accent === "rose"    ? "border-rose-200 bg-rose-50/30"
                 : accent === "cyan"    ? "border-cyan-300 bg-cyan-50/40 ring-1 ring-cyan-200/60"
                 : accent === "emerald" ? "border-emerald-200 bg-emerald-50/30"
                 : "border-slate-200 bg-slate-50/40";
  return (
    <div className={`rounded-xl border p-4 ${accentCls}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">{kicker}</div>
        {a && <SeverityBadge severity={sev} />}
      </div>
      <div className="font-display text-base font-semibold text-slate-900">{label}</div>
      {a ? (
        <div className="mt-3 flex items-center gap-3">
          <Mini5x5 likelihood={a.likelihood} impact={a.impact} severity={sev} />
          <div className="flex-1 grid grid-cols-2 gap-1.5">
            <div>
              <div className="text-[9.5px] font-mono uppercase text-slate-400 tracking-widest">Likelihood</div>
              <div className="font-mono font-bold text-xl tabular-nums text-slate-900 leading-tight">{a.likelihood}</div>
            </div>
            <div>
              <div className="text-[9.5px] font-mono uppercase text-slate-400 tracking-widest">Impact</div>
              <div className="font-mono font-bold text-xl tabular-nums text-slate-900 leading-tight">{a.impact}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9.5px] font-mono uppercase text-slate-400 tracking-widest">Score</div>
            <div className="font-mono font-extrabold text-3xl tabular-nums leading-tight" style={{ color: s.hex }}>{score}</div>
          </div>
        </div>
      ) : <div className="text-sm text-slate-500 mt-2">Not set</div>}
    </div>
  );
}

// Compact arrow / delta chip for inter-score transitions
function DeltaChip({ from, to, label }) {
  const delta = (to || 0) - (from || 0);
  const tone = delta < 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
             : delta > 0 ? "text-rose-700 bg-rose-50 border-rose-200"
             : "text-slate-600 bg-slate-50 border-slate-200";
  return (
    <div className="hidden lg:flex flex-col items-center gap-1 shrink-0 px-1" title={`${label}: ${from} → ${to}`}>
      <ArrowRight className="h-4 w-4 text-slate-400" />
      <span className={`text-[10px] font-mono font-semibold border rounded-full px-1.5 py-0.5 tabular-nums ${tone}`}>
        {delta > 0 ? "+" : ""}{delta}
      </span>
    </div>
  );
}

const ACTION_META = {
  "risk.created":    { icon: Sparkle,      color: "text-cyan-700",     bg: "bg-cyan-50",     ring: "ring-cyan-200" },
  "risk.updated":    { icon: Pencil,       color: "text-slate-700",    bg: "bg-slate-100",   ring: "ring-slate-200" },
  "risk.reviewed":   { icon: CheckCircle2, color: "text-emerald-700",  bg: "bg-emerald-50",  ring: "ring-emerald-200" },
  "finding.created": { icon: AlertTriangle,color: "text-amber-700",    bg: "bg-amber-50",    ring: "ring-amber-200" },
  "mitigation.created": { icon: Wrench,    color: "text-cyan-800",     bg: "bg-cyan-50",     ring: "ring-cyan-200" },
  "acceptance.requested": { icon: Send,    color: "text-violet-700",   bg: "bg-violet-50",   ring: "ring-violet-200" },
};

export default function RiskDetail() {
  const { id } = useParams();
  const { can, user } = useAuth();
  const nav = useNavigate();
  const [risk, setRisk] = useState(null);
  const [findings, setFindings] = useState([]);
  const [audit, setAudit] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAccept, setOpenAccept] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [mitigations, setMitigations] = useState([]);
  const [mitOpen, setMitOpen] = useState(false);
  const [editMit, setEditMit] = useState(null);
  const [history, setHistory] = useState(null);
  const [reviewing, setReviewing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, f, m, h] = await Promise.all([
        api.get(`/risks/${id}`),
        api.get(`/findings?risk_id=${id}`),
        api.get(`/mitigations?risk_id=${id}`),
        api.get(`/risks/${id}/score-history`).catch(() => ({ data: null })),
      ]);
      setRisk(r.data); setFindings(f.data); setMitigations(m.data); setHistory(h.data);
    } catch (e) { toast.error(apiError(e)); nav("/risks"); }
    finally { setLoading(false); }
  };

  const markReviewed = async () => {
    setReviewing(true);
    try { await api.post(`/risks/${id}/review`); toast.success("Marked as reviewed"); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setReviewing(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); api.get("/registers").then((r) => setRegisters(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!can("audit.read") || !risk) return;
    api.get("/audit?limit=200").then((r) => setAudit(r.data.filter((e) => e.entity_id === risk.id))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, risk]);

  const scoreSummary = useMemo(() => {
    if (!risk) return null;
    const inh = (risk.inherent?.likelihood || 0) * (risk.inherent?.impact || 0);
    const cur = risk.current_score || (risk.current?.likelihood || 0) * (risk.current?.impact || 0);
    const tgt = (risk.target?.likelihood || 0) * (risk.target?.impact || 0);
    const dropNow = inh > 0 ? Math.round(((inh - cur) / inh) * 100) : 0;
    const dropPlanned = inh > 0 ? Math.round(((inh - tgt) / inh) * 100) : 0;
    return { inh, cur, tgt, dropNow, dropPlanned };
  }, [risk]);

  if (loading || !risk) return <div className="p-8 max-w-7xl mx-auto"><LoadingSkeleton rows={5} /></div>;

  const curSev = risk.current_severity || severityFromScore(risk.current_score || 0);
  const curSevHex = SEV_HEX[curSev];
  const register = registers.find((r) => r.id === risk.register_id);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="risk-detail-page">
      <button onClick={() => nav("/risks")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4 transition-colors" data-testid="back-to-risks">
        <ArrowLeft className="h-4 w-4" /> Back to register
      </button>

      {/* ===== Hero ===== */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0A1329] to-[#111E42] text-white mb-6 shadow-sm">
        <div className="absolute -top-20 -right-16 w-[320px] h-[320px] rounded-full bg-cyan-500/10 blur-2xl pointer-events-none" aria-hidden />
        {/* Severity accent stripe */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: curSevHex, boxShadow: `0 0 24px ${curSevHex}80` }} aria-hidden />

        <div className="relative pl-7 pr-6 py-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1 max-w-3xl">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-cyan-200 bg-cyan-500/10 border border-cyan-400/30 rounded-full px-2 py-0.5">
                  <Hash className="h-3 w-3" /> {risk.code}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-white/70 bg-white/10 border border-white/15 rounded-full px-2 py-0.5">
                  {risk.category}
                </span>
                {register && (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-white/70 bg-white/10 border border-white/15 rounded-full px-2 py-0.5">
                    {register.name}
                  </span>
                )}
                <StatusPill status={risk.status} />
                {risk.appetite_breach && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-rose-500/20 text-rose-200 border border-rose-400/40" data-testid="appetite-breach-badge">
                    <AlertOctagon className="h-3 w-3" /> Appetite breach
                  </span>
                )}
                {risk.is_overdue_review && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-amber-500/20 text-amber-100 border border-amber-400/40" data-testid="overdue-review-badge">
                    <Clock className="h-3 w-3" /> Overdue review · {risk.days_since_review}d
                  </span>
                )}
              </div>
              <h1 className="font-display font-bold text-[32px] leading-tight tracking-tight break-words">{risk.title}</h1>
              <p className="mt-2 text-white/70 text-[14px] leading-relaxed break-words max-w-2xl">{risk.statement}</p>
              <div className="mt-4 flex items-center gap-4 text-[12px] text-white/70 flex-wrap">
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-white/15 border border-white/20 text-[10px] font-mono font-semibold">
                    {_initials(risk.owner_name)}
                  </span>
                  {risk.owner_name || "Unassigned"}
                </span>
                {risk.financial_impact != null && (
                  <span className="inline-flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> {Number(risk.financial_impact).toLocaleString()}</span>
                )}
                {risk.last_reviewed_at && (
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Reviewed {formatDate(risk.last_reviewed_at)}</span>
                )}
                <span className="inline-flex items-center gap-1.5"><Pencil className="h-3.5 w-3.5" /> Updated {formatDateTime(risk.updated_at)}</span>
              </div>
            </div>

            {/* Action rail */}
            <div className="flex items-center gap-2 shrink-0">
              {can("risk.write") && (
                <Button variant="outline" onClick={markReviewed} disabled={reviewing} className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="btn-mark-reviewed">
                  <Sparkle className="h-3.5 w-3.5 mr-1.5" /> {reviewing ? "Saving…" : "Mark reviewed"}
                </Button>
              )}
              {can("risk.write") && (
                <Button variant="outline" onClick={() => setEditOpen(true)} className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="btn-edit-risk">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
              )}
              {can("acceptance.request") && risk.appetite_breach && risk.status === "Active" && (
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold shadow-[0_0_18px_rgba(34,211,238,0.4)]" onClick={() => setOpenAccept(true)} data-testid="btn-request-acceptance">
                  <Send className="h-4 w-4 mr-1.5" /> Request acceptance
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Treatment reduction summary ===== */}
      {scoreSummary && scoreSummary.inh > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5" data-testid="treatment-summary">
          <SummaryKpi icon={Shield}       label="Reduction via existing controls" value={`${scoreSummary.dropNow}%`}    hint={`${scoreSummary.inh} → ${scoreSummary.cur}`} tone="emerald" />
          <SummaryKpi icon={TargetIcon}   label="Reduction once treatment lands"   value={`${scoreSummary.dropPlanned}%`} hint={`${scoreSummary.inh} → ${scoreSummary.tgt}`} tone="cyan" />
          <SummaryKpi icon={TrendingDown} label="Residual gap to target"           value={Math.max(0, scoreSummary.cur - scoreSummary.tgt)} hint={`${scoreSummary.cur} today · aiming for ${scoreSummary.tgt}`} tone="slate" />
        </div>
      )}

      {/* ===== Three score cards with transition arrows between them ===== */}
      <div className="relative mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 lg:gap-1 items-stretch" data-testid="score-cards">
          <ScoreCard kicker="Inherent" label="Before controls"            a={risk.inherent} accent="rose" />
          <DeltaChip from={scoreSummary?.inh} to={scoreSummary?.cur} label="Inherent → Current" />
          <ScoreCard kicker="Current"  label="With existing controls"     a={risk.current}  accent="cyan" />
          <DeltaChip from={scoreSummary?.cur} to={scoreSummary?.tgt} label="Current → Target" />
          <ScoreCard kicker="Target"   label="After treatment"            a={risk.target}   accent="emerald" />
        </div>
      </div>

      {/* ===== Score history sparkline ===== */}
      {history && history.points && history.points.length > 1 && (
        <Card className="p-4 mb-6 border-slate-200/80" data-testid="score-history">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono font-semibold text-cyan-700">Trend · derived from audit trail</div>
              <div className="font-display font-semibold text-slate-900 text-sm mt-0.5">Score history · last {history.points.length} datapoints</div>
              <div className="text-[11px] text-slate-500 mt-0.5">History beyond 30 days is purged by the audit retention policy.</div>
            </div>
            <Sparkline points={history.points} />
          </div>
        </Card>
      )}

      {/* ===== Tabs ===== */}
      <Tabs defaultValue="findings" className="mb-8">
        <TabsList data-testid="risk-tabs">
          <TabsTrigger value="findings" data-testid="tab-findings" className="gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Findings ({findings.length})</TabsTrigger>
          <TabsTrigger value="mitigations" data-testid="tab-mitigations" className="gap-1.5"><Wrench className="h-3.5 w-3.5" /> Mitigations ({mitigations.length})</TabsTrigger>
          <TabsTrigger value="treatment" data-testid="tab-treatment" className="gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Treatment plan</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="gap-1.5"><HistoryIcon className="h-3.5 w-3.5" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="findings" className="mt-4">
          {findings.length === 0 ? (
            <Card className="p-10 flex flex-col items-center justify-center text-center">
              <AlertTriangle className="h-8 w-8 text-slate-300 mb-2" />
              <div className="font-semibold text-slate-700">No findings yet</div>
              <div className="text-xs text-slate-500 mt-1">Assessment findings linked to this risk will appear here.</div>
            </Card>
          ) : (
            <div className="space-y-2.5" data-testid="findings-list">
              {findings.map((f) => {
                const sevHex = SEV_HEX[f.severity] || "#64748B";
                return (
                  <div key={f.id} className="relative overflow-hidden rounded-xl border border-slate-200 bg-white hover:border-cyan-300 hover:shadow-sm transition-all">
                    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: sevHex }} aria-hidden />
                    <div className="pl-4 pr-4 py-3.5 flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-sm break-words">{f.title}</div>
                        {f.description && <div className="text-xs text-slate-600 mt-1 leading-relaxed">{f.description}</div>}
                        {f.due_date && <div className="text-[11px] font-mono text-slate-500 mt-1.5 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Due {formatDate(f.due_date)}</div>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <SeverityBadge severity={f.severity} />
                        <StatusPill status={f.status} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mitigations" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest font-mono font-semibold text-cyan-700">
              {mitigations.length} recorded · {mitigations.filter((m) => m.status === "Implemented" || m.status === "Verified").length} implemented
            </div>
            {can("risk.write") && <Button size="sm" className="bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => { setEditMit(null); setMitOpen(true); }} data-testid="btn-new-mitigation"><Wrench className="h-3.5 w-3.5 mr-1.5" /> Add mitigation</Button>}
          </div>
          {mitigations.length === 0 ? (
            <Card className="p-10 flex flex-col items-center justify-center text-center">
              <Wrench className="h-8 w-8 text-slate-300 mb-2" />
              <div className="font-semibold text-slate-700">No mitigations yet</div>
              <div className="text-xs text-slate-500 mt-1 max-w-md">Record the controls, projects or actions that are reducing this risk.</div>
            </Card>
          ) : (
            <div className="space-y-2.5" data-testid="mitigations-list">
              {mitigations.map((m) => {
                const effTone = m.effectiveness === "High" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                              : m.effectiveness === "Medium" ? "text-amber-700 bg-amber-50 border-amber-200"
                              : "text-slate-700 bg-slate-50 border-slate-200";
                return (
                  <button key={m.id} onClick={() => can("risk.write") && (setEditMit(m), setMitOpen(true))} className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-cyan-300 hover:shadow-sm transition-all" data-testid={`mit-${m.id.slice(0,8)}`}>
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center shrink-0">
                        <Wrench className="h-4 w-4 text-cyan-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="font-semibold text-slate-900 text-sm">{m.title}</div>
                          <div className="flex items-center gap-1.5">
                            <StatusPill status={m.status} />
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${effTone}`}>{m.effectiveness} effectiveness</span>
                          </div>
                        </div>
                        {m.description && <div className="text-xs text-slate-600 mt-1 leading-relaxed">{m.description}</div>}
                        <div className="text-[11px] font-mono text-slate-500 mt-2 flex items-center gap-3">
                          <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" /> {m.owner || "Unassigned"}</span>
                          {m.due_date && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Due {formatDate(m.due_date)}</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="treatment" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center">
                <ListChecks className="h-4 w-4 text-cyan-700" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono font-semibold text-cyan-700">Plan</div>
                <div className="font-display font-semibold text-slate-900">What we're doing about this risk</div>
              </div>
            </div>
            {risk.treatment_plan ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 text-slate-800 whitespace-pre-line text-sm leading-relaxed">{risk.treatment_plan}</div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/30 p-6 text-center">
                <div className="text-sm text-slate-600">No treatment plan documented.</div>
                {can("risk.write") && <Button variant="outline" size="sm" className="mt-3" onClick={() => setEditOpen(true)}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Add treatment plan</Button>}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {audit.length === 0 ? (
            <Card className="p-10 flex flex-col items-center justify-center text-center">
              <HistoryIcon className="h-8 w-8 text-slate-300 mb-2" />
              <div className="font-semibold text-slate-700">No history recorded</div>
              <div className="text-xs text-slate-500 mt-1">Changes to this risk appear here with a hash-chained audit trail.</div>
            </Card>
          ) : (
            <Card className="p-6" data-testid="history-timeline">
              <div className="relative">
                <div className="absolute left-[18px] top-2 bottom-2 w-px bg-slate-200" aria-hidden />
                <div className="space-y-4">
                  {audit.map((e) => {
                    const meta = ACTION_META[e.action] || { icon: Pencil, color: "text-slate-700", bg: "bg-slate-50", ring: "ring-slate-200" };
                    const Icon = meta.icon;
                    return (
                      <div key={e.id} className="relative flex gap-4 items-start">
                        <div className={`relative z-10 h-9 w-9 rounded-full ${meta.bg} ring-4 ${meta.ring} flex items-center justify-center shrink-0`}>
                          <Icon className={`h-4 w-4 ${meta.color}`} />
                        </div>
                        <div className="flex-1 min-w-0 pb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-slate-900">{e.action.replace(/\./g, " · ")}</span>
                            <span className="text-[11px] text-slate-500">by {e.actor_email}</span>
                            <span className="text-[11px] font-mono text-slate-400 ml-auto">#{e.seq} · {formatDateTime(e.created_at)}</span>
                          </div>
                          <div className="text-[10.5px] font-mono text-slate-400 mt-0.5" title={e.hash}>hash {e.hash.slice(0, 12)}…</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {openAccept && <AcceptanceDialog risk={risk} users={users} me={user} onClose={() => setOpenAccept(false)} onSubmitted={() => { setOpenAccept(false); load(); }} />}
      {editOpen && <RiskFormDialog registers={registers} existing={risk} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
      {mitOpen && <MitigationDialog riskId={risk.id} existing={editMit} onClose={() => setMitOpen(false)} onSaved={() => { setMitOpen(false); load(); }} />}
    </div>
  );
}

function SummaryKpi({ icon: Icon, label, value, hint, tone }) {
  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    cyan:    "border-cyan-200 bg-cyan-50/40 text-cyan-700",
    slate:   "border-slate-200 bg-slate-50/40 text-slate-700",
  }[tone] || "border-slate-200 bg-slate-50/40 text-slate-700";
  return (
    <div className={`rounded-xl border p-4 ${toneCls.replace(/text-\w+-\d+/, "")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-[10px] uppercase tracking-widest font-mono font-semibold ${toneCls.match(/text-\w+-\d+/)?.[0] || ""}`}>{label}</div>
          <div className="font-display font-extrabold text-3xl text-slate-900 tabular-nums mt-0.5">{value}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 font-mono tabular-nums">{hint}</div>
        </div>
        <Icon className={`h-5 w-5 ${toneCls.match(/text-\w+-\d+/)?.[0] || "text-slate-400"}`} />
      </div>
    </div>
  );
}

function MitigationDialog({ riskId, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: existing?.title || "", description: existing?.description || "",
    owner: existing?.owner || "", status: existing?.status || "Planned",
    effectiveness: existing?.effectiveness || "Medium",
    due_date: existing?.due_date ? existing.due_date.slice(0, 10) : "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = { ...form, risk_id: riskId, due_date: form.due_date ? new Date(form.due_date).toISOString() : null };
      if (existing) await api.patch(`/mitigations/${existing.id}`, payload);
      else await api.post("/mitigations", payload);
      toast.success(existing ? "Mitigation updated" : "Mitigation added");
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!existing || !window.confirm("Delete mitigation?")) return;
    try { await api.delete(`/mitigations/${existing.id}`); toast.success("Deleted"); onSaved?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="mit-dialog">
        <DialogHeader><DialogTitle className="font-display">{existing ? "Edit mitigation" : "Add mitigation"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mit-title" /></div>
          <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Owner</Label><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Planned", "InProgress", "Implemented", "Verified"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Effectiveness</Label>
              <Select value={form.effectiveness} onValueChange={(v) => setForm({ ...form, effectiveness: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Low", "Medium", "High"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          {existing && <Button variant="ghost" className="mr-auto text-rose-700" onClick={del}>Delete</Button>}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="mit-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcceptanceDialog({ risk, users, me, onClose, onSubmitted }) {
  const [approverId, setApproverId] = useState("");
  const [expires, setExpires] = useState(() => new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10));
  const [just, setJust] = useState("");
  const [busy, setBusy] = useState(false);
  const eligible = users.filter((u) => (u.role === "Approver" || u.role === "Admin") && u.id !== me.id && u.id !== risk.owner_id);
  const submit = async () => {
    if (!approverId || !just) return toast.error("Approver and justification required");
    setBusy(true);
    try {
      await api.post("/acceptances", { risk_id: risk.id, justification: just, expires_at: new Date(expires).toISOString(), approver_id: approverId });
      toast.success("Acceptance requested"); onSubmitted?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="acceptance-dialog">
        <DialogHeader><DialogTitle className="font-display">Request risk acceptance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Approver</Label>
            <Select value={approverId} onValueChange={setApproverId}>
              <SelectTrigger data-testid="acc-approver"><SelectValue placeholder="Select an approver" /></SelectTrigger>
              <SelectContent>
                {eligible.length === 0 ? (
                  <div className="px-2 py-1 text-xs text-slate-500">No eligible approvers (must be Approver/Admin and not you or the risk owner).</div>
                ) : eligible.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Expires on</Label><Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="acc-expires" /></div>
          <div><Label>Justification</Label><Textarea rows={3} value={just} onChange={(e) => setJust(e.target.value)} placeholder="Why is accepting this risk the right decision?" data-testid="acc-justification" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="acc-submit">Submit request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
